// eslint-disable-next-line jira/restricted/react
import React, { PureComponent, type ComponentType } from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import Heading from '@atlaskit/heading';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { borderRadius } from '@atlassian/jira-common-styles/src/main.tsx';
import type { IssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import { DEFAULT_LIST_WIDTH, type ItemProject } from '../../../../model/table';
import createRaf from '../../../../utils/create-request-animation-frame.tsx';
import type {
	TableCommonProps,
	ListItem as ListItemType,
	OnItemExpandChanged,
} from '../../common/types';
import { getProjectItemTypeForLevel } from '../../common/utils/projects.tsx';
import type { APIProps as ListItemContentProps } from '../list-item-content';
import type { APIProps as ListItemContentTooltipProps } from '../list-item-content-tooltip';
import type { APIProps as ListItemProps } from '../list-item/view.tsx';
import Items from './items';
import ListResizer from './resizable';
import ScrollArea from './scroll-area/view.tsx';

const requestAnimationFrame = createRaf();

type ListComponents = {
	ListItem: ComponentType<ListItemProps>;
	ListItemContent: ComponentType<ListItemContentProps>;
	ListItemContentTooltip: ComponentType<ListItemContentTooltipProps>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	NoMatches: ComponentType<Record<any, any>>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	Loader: ComponentType<Record<any, any>>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	IssueTitle: ComponentType<Record<any, any>>;
};

type Props = {
	isResizeEnabled: boolean;
	items: ListItemType[];
	projects: ItemProject[];
	selectedItemId: IssueId | undefined;
	components: ListComponents;
} & TableCommonProps & {
		listWidth: number;
		maxListWidth: number;
		itemHeight: number;
		onItemExpandChanged: OnItemExpandChanged;
		onSetListWidth: (width: number) => void;
	};

type State = {
	width: number;
};

// eslint-disable-next-line jira/react/no-class-components
class List extends PureComponent<Props, State> {
	// go/jfe-eslint
	// eslint-disable-next-line react/sort-comp
	static whyDidYouRender = true;

	static defaultProps = {
		isSelectEnabled: true,
		isHoverEnabled: false,
		items: [],
		expandedItems: {},
		selectedItemId: undefined,
		listWidth: DEFAULT_LIST_WIDTH,
		onItemClick: noop,
		onItemExpandChanged: noop,
		onSetListWidth: noop,
	};

	static getDerivedStateFromProps(props: Props, state: State) {
		if (props.isResizeEnabled && state.width > props.maxListWidth) {
			return { width: props.maxListWidth };
		}
		return null;
	}

	constructor(props: Props) {
		super(props);
		this.state = {
			width: props.listWidth,
		};
	}

	onResize = (width: number) => {
		const { width: currentWidth } = this.state;
		const { maxListWidth } = this.props;

		const newWidth = Math.min(width, maxListWidth);

		if (currentWidth !== newWidth) {
			requestAnimationFrame(() => {
				this.setState({ width: newWidth });
			});
		}
	};

	onEndResize = () => {
		this.props.onSetListWidth(this.state.width);
	};

	renderItems() {
		const {
			isSelectEnabled,
			items,
			itemHeight,
			expandedItems,
			selectedItemId,
			activeLevel,
			components: { ListItem, ListItemContent, ListItemContentTooltip },
			onItemClick,
			onItemExpandChanged,
			projects,
		} = this.props;

		const typeName = getProjectItemTypeForLevel(projects, projects[0].id, activeLevel).name;

		return (
			<Items
				isSelectEnabled={isSelectEnabled}
				items={items}
				itemHeight={itemHeight}
				expandedItems={expandedItems}
				selectedItemId={selectedItemId}
				activeLevel={activeLevel}
				components={{
					ListItem,
					ListItemContent,
					ListItemContentTooltip,
				}}
				onItemClick={onItemClick}
				onItemExpandChanged={onItemExpandChanged}
				typeName={typeName}
				projects={projects}
			/>
		);
	}

	render() {
		const { items, isResizeEnabled, components } = this.props;

		const { width: listWidth } = this.state;
		const listWidthWithoutBorder = listWidth - 2;

		const isListEmpty = items.length === 0;
		return (
			<Container
				width={listWidth}
				data-testid="deployments.common.components.table.components.list.container"
			>
				<Heading size="xsmall">
					<Header>
						<components.IssueTitle />
						{isResizeEnabled ? (
							<ListResizer
								width={listWidth}
								onResize={this.onResize}
								onEndResize={this.onEndResize}
							/>
						) : null}
					</Header>
				</Heading>
				<Content>
					<ScrollArea isScrollable={!isListEmpty}>
						<WidthCorrector width={listWidthWithoutBorder}>
							{this.renderItems()}
							{!isListEmpty && (
								<ItemsBottomPadding>
									<components.Loader />
								</ItemsBottomPadding>
							)}
							{isListEmpty && <components.NoMatches />}
						</WidthCorrector>
					</ScrollArea>
					<HeightCorrector width={listWidthWithoutBorder} />
				</Content>
			</Container>
		);
	}
}

interface ContainerProps {
	width: number;
}

const borderWidth = 1;
const primaryBorder = `${borderWidth}px solid ${token('color.border', colors.N30)}`;
const secondaryBorder = `${borderWidth}px solid ${token('color.border', colors.N40)}`;

interface ItemsBottomPaddingProps {
	invisible?: boolean;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ItemsBottomPadding = styled.div<ItemsBottomPaddingProps>({
	height: '88px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.text.inverse', colors.N0),
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	opacity: (props: ItemsBottomPaddingProps) => (props.invisible ? 0 : 1),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div<ContainerProps>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ width }: ContainerProps) => `${width}px`,
	flex: '0 0 auto',
	display: 'flex',
	flexDirection: 'column',
	position: 'relative',
	height: '100%',
	minHeight: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Header = styled.div({
	width: '100%',
	height: '56px',
	boxSizing: 'border-box',
	borderBottom: secondaryBorder,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.neutral', colors.N20),
	marginTop: 0,
	flex: '0 0 auto',
	display: 'flex',
	alignItems: 'center',
	padding: `0 ${token('space.200', '16px')} 0 ${token('space.300', '24px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${borderRadius}px 0 0 0`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N500),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Content = styled.div({
	position: 'relative',
	flex: '1 1 auto',
	flexDirection: 'column',
	display: 'flex',
	marginLeft: token('space.negative.100', '-8px'),
	overflow: 'hidden',
});

interface WidthCorrectorProps {
	width: number;
}

/* Set the width for this explicitly (adjusted for border).
 * We want to correct any calculations based on the hidden scroll parent,
 * whose width is affected by the padding and scrollbar visibility.
 * We cannot fix this through border-box since we need the padding to be
 * excluded from the width to hide the scrollbar.
 */
// RLSS-2812 replace color.background.neutral into color.background.accent.gray.subtlest
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WidthCorrector = styled.div<WidthCorrectorProps>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: (props: WidthCorrectorProps) => `${props.width}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.neutral', colors.N50A),
	marginLeft: token('space.100', '8px'),
});

interface HeightCorrectorProps {
	width: number;
	hideLeftBorder?: boolean;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeightCorrector = styled.div<HeightCorrectorProps>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: (props: HeightCorrectorProps) => `${props.width}px`,
	flex: '1 1 auto',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	borderLeft: (props: HeightCorrectorProps) => (props.hideLeftBorder ? '0' : primaryBorder),
	borderRight: primaryBorder,
	marginLeft: token('space.100', '8px'),
});

export default List;

export type { Props as APIProps };
